import type { DesignerNodeData, GetNextId } from "@skilitics/nautilus-core-designer-data";

import type { GetNodeRole } from "../../state/getNodeRole.ts";
import type { GetNodeTitle } from "../../state/getNodeTitle.ts";
import { resolveNodeProfileCreate, withProfileChildNodes } from "../../state/nodePublicApi.ts";
import type { DesignerNode, GetManifestByTypeId, GetPlaceholderManifest, Obj } from "../../types/index.ts";
import { applyDefinitionCreate } from "./applyDefinition.ts";
import type { CreateNodeData } from "./shared.ts";

type Options = {
  getManifest: GetManifestByTypeId;
  getPlaceholderManifest: GetPlaceholderManifest;
  getNextId: GetNextId;
  getNodeRole?: GetNodeRole;
  getNodeTitle?: GetNodeTitle;
};

export type CreateOptions = {
  definitionId?: string;
  owner?: DesignerNodeData["owner"];
};

export type CreateDesignerNode<T, TOptions extends CreateOptions = Obj> = (typeId: string, options?: TOptions) => T;

export const createNodeCreate = ({
  getManifest,
  getPlaceholderManifest,
  getNextId,
  getNodeTitle,
  getNodeRole,
}: Options): CreateDesignerNode<DesignerNode> => {
  // nodes being created are not in state yet, so profile resolves by object
  const resolveNodeProfile = resolveNodeProfileCreate({ getManifest });

  const nodeCreate: CreateDesignerNode<DesignerNode, CreateOptions> = (typeId, options = {}) => {
    const {
      designer: { getInitialConfig, getInitialChildNodes },
      version,
      terminus: { placeholder: isPlaceholder },
    } = getManifest(typeId);

    const id = getNextId();

    const config = getInitialConfig(options);

    const childNodeCreate: CreateDesignerNode<DesignerNode, CreateOptions> = (childTypeId, childOptions) =>
      nodeCreate(childTypeId, { ...childOptions, owner: options.owner });

    const childNodes =
      getInitialChildNodes?.({
        id,
        createNode: childNodeCreate,
        createPlaceholder: (selector, params) => {
          const manifest = getPlaceholderManifest(selector);
          return nodeCreate(manifest.typeId, params);
        },
        options,
      }) ?? {};

    const role = getNodeRole?.({ typeId });

    const creator = options.owner;

    const basic: Parameters<GetNodeTitle>[0] = {
      id,
      typeId,
      config,
      childNodes,
      owner: creator,
      creator,
    };

    const node = {
      ...basic,
      version,
      role,
      isPlaceholder,
      title: getNodeTitle?.(withProfileChildNodes(basic, resolveNodeProfile)),
    };

    if (options?.definitionId) {
      const applyDefinition = applyDefinitionCreate(nodeCreate, getManifest, getNodeTitle);

      return applyDefinition(node, options.definitionId);
    }

    return node;
  };

  return nodeCreate;
};

// This is just for typing for now but this may end up different.
// Currently, we can get away with this because a monolingual config is still valid
// When we update the config of a node is when we want multilingual, and it gets done automatically then
export const createNodeDataCreate = createNodeCreate as (options: Options) => CreateNodeData;
